import dayjs from 'dayjs';
import { ChevronRight } from 'lucide-react';
import {
  useGetRecentOrdersForPropertyQuery,
} from '@/components/orders/services/orderApi';
import type { OrderResponse } from '@/types/order.types';
import { getStatusTitle } from '@/components/orders/table-cell/orderCellService';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import {
  STATUS_TONE_CLASSES,
  statusToTone,
} from '../property-view/metadata/propertyConfig';

type Props = {
  propertyId: string;
  open: boolean;
  onViewAll: () => void;
};

const SectionHeading = ({
  children,
  count,
}: {
  children: React.ReactNode;
  count?: number;
}) => (
  <Typography
    as="h3"
    variant="small-semibold"
    className="uppercase tracking-wider text-muted-foreground"
  >
    {children}
    {typeof count === 'number' ? ` (${count})` : ''}
  </Typography>
);

const OrderRow = ({ order }: { order: OrderResponse }) => {
  const tone = statusToTone(order.orderStatus);
  const date = order.orderDate
    ? dayjs(order.orderDate).format('MMM DD, YYYY')
    : '—';
  const serviceSummary =
    order.services && order.services.length > 0
      ? order.services.map((s) => s.name).filter(Boolean).join(', ')
      : '—';
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <Typography as="div" variant="body-medium" className="truncate">
          {order.orderNumber ?? '—'}
        </Typography>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 shrink-0',
            STATUS_TONE_CLASSES[tone],
          )}
        >
          <Typography
            as="span"
            variant="small-semibold"
            className="uppercase tracking-wider"
          >
            {order.orderStatus
              ? (getStatusTitle(order.orderStatus) || order.orderStatus)
              : '—'}
          </Typography>
        </span>
      </div>
      <Typography
        as="div"
        variant="small"
        className="text-muted-foreground truncate"
      >
        {serviceSummary} · {date}
      </Typography>
    </div>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="rounded-md border border-dashed border-border px-4 py-3">
    <Typography as="p" variant="small" className="text-muted-foreground">
      {message}
    </Typography>
  </div>
);

export const RecentOrdersSection = ({ propertyId, open, onViewAll }: Props) => {
  const query = useGetRecentOrdersForPropertyQuery(
    { propertyId, size: 3 },
    { skip: !open || !propertyId },
  );

  const total = query.data?.totalElementsAfterFilter ?? 0;
  const orders = query.data?.content ?? [];

  let body: React.ReactNode;
  if (query.isLoading) {
    body = (
      <div className="space-y-2" data-testid="orders-loading">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-md" />
        ))}
      </div>
    );
  } else if (query.isError) {
    body = (
      <div
        className="space-y-2 rounded-md border border-dashed border-destructive/30 px-4 py-3"
        data-testid="orders-error"
      >
        <Typography as="p" variant="small" className="text-muted-foreground">
          Couldn&apos;t load recent orders
        </Typography>
        {/* Standalone action (own row, not inline prose) — carries the 44px
            floor the link variant deliberately omits (see button.tsx). */}
        <Button variant="link" className="max-lg:min-h-11" onClick={() => query.refetch()}>
          Retry
        </Button>
      </div>
    );
  } else if (orders.length === 0) {
    body = (
      <EmptyState message="No orders yet — use Add to cart to begin." />
    );
  } else {
    body = (
      <div className="space-y-2">
        {orders.map((o) => (
          <OrderRow key={o.orderId} order={o} />
        ))}
      </div>
    );
  }

  return (
    <section className="space-y-3">
      <SectionHeading count={query.isSuccess ? total : undefined}>
        Orders
      </SectionHeading>
      {body}
      {/* Standalone action — same call-site floor as Retry above. */}
      {query.isSuccess && orders.length > 0 && (
        <Button variant="link" className="max-lg:min-h-11" onClick={onViewAll}>
          View all
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
    </section>
  );
};
